import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Center,
  Flex,
  Group,
  Paper,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { ToLeftIcon, ToRightIcon } from "../../shared/svg";
import { IconPlus } from "../../shared/svg/AddIcon";
import { Goal, GoalTimestamp } from "../../types/api";
import { useMediaQuery } from "@mantine/hooks";
import BotMessageSquareIcon from "../../shared/svg/BotMessage";
import { GoalItem } from "../../features/calendar/calendar-utils/GoalItem";
import { AITooltip } from "../../utils/components/AITooltip";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";
import useProfileStore from "../../store/useProfileStore";

type CalendarYearProps = {
  currentYear: Dayjs;
  changeYear: (direction: "prev" | "next") => void;
  daysInMonth?: number[];
  goals: Goal[];
  rowHeights: number[];
  taskRowRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  commonStyles: React.CSSProperties;

  setIsOpen: (value: boolean) => void;
  setIsEditOpen: (value: boolean) => void;
  setEditGoal: (goal: Goal | null) => void;

  setIsTaskOpen: (value: boolean) => void;
  setSelectedDate: (date: Dayjs | null) => void;

  setIsEditTaskOpen: (value: boolean) => void;
  setEditTask: (task: GoalTimestamp | null) => void;
};

export default function CalendarYearView({
  currentYear,
  changeYear,
  goals,
  rowHeights,
  taskRowRefs,
  commonStyles,
  setIsOpen,
  setIsEditOpen,
  setEditGoal,
  setIsTaskOpen,
  setSelectedDate,
  setIsEditTaskOpen,
  setEditTask,
}: CalendarYearProps) {
  const { t, i18n } = useTranslation();
  const theme = useMantineTheme();
  const matches = useMediaQuery("(min-width: 990px)");
  const todayRef = useRef<HTMLDivElement>(null);
  const firstDayRef = useRef<HTMLDivElement>(null);
  const isTouchDevice =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none) and (pointer: coarse)").matches;

  const [hovered, setHovered] = useState<"main" | "ai" | null>(null);

  const year = useMemo(() => currentYear.year(), [currentYear]);

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) =>
      dayjs().locale(i18n.language).year(year).month(i)
    );
  }, [year, i18n.language]);

  const { profile } = useProfileStore();
  useEffect(() => {
    if (!matches) return;
    if (!profile?.display_preferences.hide_tour_tooltip_on_dev_plan) return;

    if (todayRef.current) {
      todayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    } else if (firstDayRef.current) {
      firstDayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [
    matches,
    currentYear,
    profile?.display_preferences.hide_tour_tooltip_on_dev_plan,
  ]);

  useEffect(() => {
    if (!matches) return;
    if (
      firstDayRef.current &&
      profile?.display_preferences.hide_tour_tooltip_on_dev_plan !==
        undefined &&
      !profile?.display_preferences.hide_tour_tooltip_on_dev_plan
    ) {
      firstDayRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [profile?.display_preferences.hide_tour_tooltip_on_dev_plan, matches]);

  const calendarRef = useRef<HTMLDivElement>(null);
  const [dayWidth, setDayWidth] = useState(100);

  useEffect(() => {
    const updateDayWidth = () => {
      if (calendarRef.current) {
        const totalWidth = calendarRef.current.clientWidth;
        const newDayWidth = Math.floor(totalWidth / 12);
        setDayWidth(Math.max(100, newDayWidth));
      }
    };

    updateDayWidth();
    window.addEventListener("resize", updateDayWidth);
    return () => window.removeEventListener("resize", updateDayWidth);
  }, []);

  return (
    <Box>
      <Group gap={24} align="center">
        <OnboardingTour.Target id="calendar-period">
          <Flex gap={24} align="center" id="calendar-period" px={5}>
            <span
              onClick={() => changeYear("prev")}
              style={{ cursor: "pointer" }}
            >
              <ToLeftIcon />
            </span>
            <Title size={24}>{`${year} ${t("year")}`}</Title>
            <span
              onClick={() => changeYear("next")}
              style={{ cursor: "pointer" }}
            >
              <ToRightIcon />
            </span>
          </Flex>
        </OnboardingTour.Target>
      </Group>

      <Paper className="calendar" withBorder radius={16} mt={32}>
        {/* Goals List */}
        <Box w="276px" style={{ borderRight: "1px solid #d6d6d6" }}>
          <Text px={11} py={16} c="#737373" h={58} className="goal-item">
            {t("goals")}
          </Text>
          {goals.map((goal, index) => {
            return index === 0 ? (
              <OnboardingTour.Target id="calendar-goal">
                <div key={goal.id} id="calendar-goal">
                  <GoalItem
                    goal={goal}
                    height={rowHeights[index] || 58}
                    onClickTitle={(goal) => {
                      setEditGoal(goal);
                      setIsEditOpen(true);
                    }}
                    onClickAddTask={(goal) => {
                      setEditGoal(goal);
                      setIsTaskOpen(true);
                    }}
                  />
                </div>
              </OnboardingTour.Target>
            ) : (
              <GoalItem
                goal={goal}
                key={goal.id}
                height={rowHeights[index] || 58}
                onClickTitle={(goal) => {
                  setEditGoal(goal);
                  setIsEditOpen(true);
                }}
                onClickAddTask={(goal) => {
                  setEditGoal(goal);
                  setIsTaskOpen(true);
                }}
              />
            );
          })}
          <Box px={11}>
            <OnboardingTour.Target id="calendar-add-goal">
              <Flex
                className="goal-input"
                w="254px"
                align="center"
                id="calendar-add-goal"
                gap={8}
              >
                <Button
                  variant="outline"
                  radius="xl"
                  onClick={() => setIsOpen(true)}
                  style={{
                    width: "100%",
                    padding: "8px 12px 0 0",
                    border: "transparent",
                    background: "transparent",
                    color: "#737373",
                    display: "flex",
                    fontWeight: 400,
                  }}
                >
                  <label htmlFor="goal" className="goal-icon">
                    +
                  </label>{" "}
                  {t("add_goal")}
                </Button>
              </Flex>
            </OnboardingTour.Target>
          </Box>
        </Box>

        {/* Calendar Grid */}
        <Box className="days" ref={calendarRef}>
          <Flex w="max-content">
            {months.map((month, i) => {
              const isCurrentMonth = dayjs().isSame(month, "month");
              const isFirst = i === 0;
              return (
                <Flex
                  key={i}
                  align="center"
                  justify="center"
                  ref={isCurrentMonth ? todayRef : isFirst ? firstDayRef : null}
                  className="day"
                  direction="column"
                  w={dayWidth}
                  h={58}
                  c={isCurrentMonth ? "#fff" : "#737373"}
                  bg={isCurrentMonth ? "#6F40E2" : "transparent"}
                  style={{
                    borderRadius: isCurrentMonth ? 12 : 0,
                    fontWeight: isCurrentMonth ? 700 : 400,
                  }}
                >
                  <Text style={{ textTransform: "capitalize" }}>
                    {month.format("MMMM")}
                  </Text>
                </Flex>
              );
            })}
          </Flex>

          <OnboardingTour.Target id="calendar-tasks">
            <div>
              {goals.map((goal, index) => {
                const tasks = goal.tasks || [];
                const monthSlots: GoalTimestamp[][] = Array.from(
                  { length: 12 },
                  () => []
                );

                const startOfYear = dayjs().year(year).startOf("year");
                const endOfYear = dayjs().year(year).endOf("year");

                const taskLayers: GoalTimestamp[][] = [];

                const positionedTasks = tasks
                  .filter((task) => {
                    const start = dayjs(task.day_from);
                    const end = dayjs(task.day_to);
                    return (
                      end.isAfter(startOfYear) && start.isBefore(endOfYear)
                    );
                  })
                  .map((task) => {
                    const start = dayjs(task.day_from);
                    const end = dayjs(task.day_to);
                    const clippedStart = start.isBefore(startOfYear)
                      ? startOfYear
                      : start;
                    const clippedEnd = end.isAfter(endOfYear) ? endOfYear : end;

                    let startIdx = clippedStart.month();
                    let endIdx = clippedEnd.month();

                    for (let i = startIdx; i <= endIdx; i++) {
                      monthSlots[i].push(task);
                    }

                    let layer = 0;
                    while (
                      taskLayers[layer]?.some((t) => {
                        const s = dayjs(t.day_from).month();
                        const e = dayjs(t.day_to).month();
                        return startIdx <= e && endIdx >= s;
                      })
                    ) {
                      layer++;
                    }

                    taskLayers[layer] = [...(taskLayers[layer] || []), task];
                    return {
                      task,
                      startIdx,
                      monthRange: endIdx - startIdx + 1,
                      layer,
                    };
                  });

                // 🔶 Змінено rowHeight: додано 1 шар для "+"
                const rowHeight = Math.max((taskLayers.length + 1) * 30, 58);

                return (
                  <Box
                    key={goal.id}
                    className="row"
                    id={index === 0 ? "calendar-tasks" : undefined}
                    ref={(el) => (taskRowRefs.current[index] = el)}
                    style={{ position: "relative", height: rowHeight }}
                  >
                    {/* Фонова сітка */}
                    <Flex style={{ position: "absolute" }}>
                      {months.map((month, mIdx) => (
                        <Center
                          key={mIdx}
                          w={dayWidth}
                          h={rowHeight}
                          className="day"
                          style={{
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setSelectedDate(month.startOf("month"));
                            setEditGoal(goal);
                            setIsTaskOpen(true);
                          }}
                        >
                          {monthSlots[mIdx].length === 0 && (
                            <Text
                              c="#00000034"
                              w="40px"
                              h="40px"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <IconPlus />
                            </Text>
                          )}
                        </Center>
                      ))}
                    </Flex>

                    {/* Таски */}
                    {positionedTasks.map(
                      ({ task, startIdx, monthRange, layer }) => (
                        <Box
                          key={task.id}
                          onClick={() => {
                            setEditGoal(goal);
                            setEditTask(task);
                            setIsEditTaskOpen(true);
                          }}
                          style={{
                            position: "absolute",
                            top: layer * 30,
                            left: startIdx * dayWidth,
                            width: dayWidth * monthRange,
                            height: 28,
                            backgroundColor: task.display_color_rgba,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 4,
                            border: "1px solid #ccc",
                            cursor: "pointer",
                          }}
                          onMouseEnter={() => setHovered("main")}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <Tooltip.Floating
                            label={task.title}
                            position="top"
                            offset={6}
                            disabled={isTouchDevice || hovered === "ai"}
                            styles={{
                              tooltip: {
                                ...commonStyles,
                                border: "1px solid gray",
                              },
                              arrow: {
                                backgroundColor: theme.colors.gray[1],
                                border: "1px solid gray",
                              },
                            }}
                          >
                            <Flex
                              align="center"
                              justify="center"
                              gap={4}
                              style={{ overflow: "hidden", flex: 1 }}
                            >
                              {task.autogenerated && (
                                <AITooltip
                                  icon={
                                    <Box
                                      w={24}
                                      h={24}
                                      ml={4}
                                      style={{
                                        borderRadius: "50%",
                                        backgroundColor: "white",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                      onMouseEnter={(e) => {
                                        e.stopPropagation();
                                        setHovered("ai");
                                      }}
                                      onMouseLeave={() => setHovered(null)}
                                    >
                                      <BotMessageSquareIcon
                                        size={16}
                                        color={theme.colors.violet[7]}
                                      />
                                    </Box>
                                  }
                                  title={t("generated_by_ai")}
                                />
                              )}
                              <Text
                                style={{
                                  fontSize: 12,
                                  padding: "2px 4px",
                                  width: "100%",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                  textAlign: "center",
                                }}
                              >
                                {task.title}
                              </Text>
                            </Flex>
                          </Tooltip.Floating>
                        </Box>
                      )
                    )}

                    {/* 🔶 Додатковий шар з "+" для місяців, де вже є таски */}
                    <Flex
                      style={{
                        position: "absolute",
                        top: taskLayers.length * 30,
                      }}
                    >
                      {months.map((month, mIdx) => {
                        const tasksThatMonth = monthSlots[mIdx] || [];

                        if (tasksThatMonth.length === 0) {
                          return (
                            <Box
                              key={`empty-${mIdx}`}
                              w={dayWidth}
                              h={28}
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setSelectedDate(month.startOf("month"));
                                setEditGoal(goal);
                                setIsTaskOpen(true);
                              }}
                            />
                          );
                        }

                        return (
                          <Center
                            key={`plus-${mIdx}`}
                            onClick={() => {
                              setSelectedDate(month.startOf("month"));
                              setEditGoal(goal);
                              setIsTaskOpen(true);
                            }}
                            style={{
                              cursor: "pointer",
                            }}
                            w={dayWidth}
                            h={28}
                          >
                            <Text
                              c="#00000034"
                              w="30px"
                              h="30px"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                fontSize: 16,
                              }}
                            >
                              <IconPlus />
                            </Text>
                          </Center>
                        );
                      })}
                    </Flex>
                  </Box>
                );
              })}
            </div>
          </OnboardingTour.Target>

          <Flex w="max-content">
            {months.map((_, index) => (
              <Box w={dayWidth} h={58} className="day" key={index} />
            ))}
          </Flex>
        </Box>
      </Paper>
    </Box>
  );
}
